import { type FC } from "react";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import { useTranslation } from "react-i18next";

export type MetricKey = "spend" | "impressions" | "clicks" | "conversions";
export type TrendGranularity = "daily" | "monthly";

export type TooltipValue = string | number | Array<string | number> | undefined;
export type TooltipPayloadItem = {
  name?: string | number;
  value?: TooltipValue;
};

export const normalizeTooltipValue = (value: TooltipValue) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return value ?? "";
};

export const OrganicChartTooltip: FC<{
  active?: boolean;
  label?: string | number;
  labelTitle: string;
  payload?: TooltipPayloadItem[];
  valueFormatter?: (_value: TooltipValue, _name: string) => string;
  seriesLabelForDataKey?: (_dataKey: string) => string;
}> = ({
  active,
  label,
  labelTitle,
  payload,
  valueFormatter = value => String(normalizeTooltipValue(value)),
  seriesLabelForDataKey,
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{labelTitle}:</span>
          <strong className="organic-tooltip__meta-value">
            {String(label ?? "")}
          </strong>
        </div>
        {payload.map(entry => {
          const seriesKey = String(entry.name ?? "");
          const seriesLabel = seriesLabelForDataKey
            ? seriesLabelForDataKey(seriesKey)
            : seriesKey || "Value";
          return (
            <div
              className="organic-tooltip__meta-line"
              key={`${seriesKey}-${String(entry.value)}`}
            >
              <span className="organic-tooltip__meta-label">{seriesLabel}:</span>
              <strong className="organic-tooltip__meta-value">
                {valueFormatter(entry.value, seriesKey)}
              </strong>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Matches Meta Ads color spec exactly. */
export const METRIC_COLORS: Record<MetricKey, string> = {
  spend: "#2b74e4",
  impressions: "#36cdb0",
  clicks: "#f6c15f",
  conversions: "#8d4fd3",
};

/** Grouped bar charts use the same 4-series palette as Meta. */
export const GROUPED_BAR_COLORS = {
  spend: "#2b74e4",
  impressions: "#36cdb0",
  conversions: "#8d4fd3",
  ctr: "#f6c15f",
  clicks: "#f6c15f",
} as const;

export type GoogleBarMetricKey = keyof typeof GROUPED_BAR_COLORS;

export type GoogleGroupedChartDatum = {
  label: string;
  spend: number;
  impressions: number;
  conversions: number;
  ctr: number;
  clicks: number;
  spendValue: number;
  impressionsValue: number;
  conversionsValue: number;
  ctrValue: number;
  clicksValue: number;
};

export const PIE_COLORS = CHART_COLOR_PALETTE;

const CHART_LABEL_MAX_LENGTH = 14;

export const truncateChartLabel = (value: unknown, maxLength = CHART_LABEL_MAX_LENGTH) => {
  const label = String(value ?? "").trim();
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, Math.max(1, maxLength - 1))}…`;
};

export const toFiniteNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const normalizeGroupedChartData = <T extends Record<string, string | number>>(
  rows: T[],
  labelKey: keyof T
): GoogleGroupedChartDatum[] => {
  const keys = ["spend", "impressions", "conversions", "ctr", "clicks"] as const;
  const maxByMetric = keys.reduce<Record<string, number>>(
    (acc, k) => {
      acc[k] = Math.max(...rows.map(r => toFiniteNumber(r[k])), 1);
      return acc;
    },
    { spend: 1, impressions: 1, conversions: 1, ctr: 1, clicks: 1 }
  );
  return rows.map(row => {
    const spend = toFiniteNumber(row.spend);
    const impressions = toFiniteNumber(row.impressions);
    const conversions = toFiniteNumber(row.conversions);
    const ctr = toFiniteNumber(row.ctr);
    const clicks = toFiniteNumber(row.clicks);
    return {
      label: String(row[labelKey] ?? "Unknown"),
      spend,
      impressions,
      conversions,
      ctr,
      clicks,
      spendValue: (spend / maxByMetric.spend) * 100,
      impressionsValue: (impressions / maxByMetric.impressions) * 100,
      conversionsValue: (conversions / maxByMetric.conversions) * 100,
      ctrValue: (ctr / maxByMetric.ctr) * 100,
      clicksValue: (clicks / maxByMetric.clicks) * 100,
    };
  });
};

export const GoogleGroupedBarLegend: FC<{
  items: ReadonlyArray<{ key: GoogleBarMetricKey; label: string }>;
}> = ({ items }) => {
  const { t } = useTranslation();
  return (
  <div className="paid-media-chart-legend" role="list" aria-label={t("metaAdsReport.charts.legendAria")}>
    {items.map(item => (
      <span key={item.key} className="paid-media-chart-legend__item" role="listitem">
        <span
          className="paid-media-chart-legend__swatch"
          style={{ backgroundColor: GROUPED_BAR_COLORS[item.key] }}
          aria-hidden
        />
        {item.label}
      </span>
    ))}
  </div>
  );
};
